import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { getModelLabel, VARIABLE_LABELS } from "./constants";
import { MLMetrics } from "./services";

const cellSx = {
  border: "none",
  py: 0.75,
  px: 1,
  whiteSpace: "nowrap",
  overflow: "hidden",
  fontSize: "0.8125rem",
};

const ScoreBar = ({ value, max }: { value: number; max: number }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Box
      sx={{
        height: 8,
        width: `${(value / max) * 120}px`,
        minWidth: 4,
        backgroundColor:
          value / max > 0.5 ? "chart.positive" : "chart.neutralStroke",
        borderRadius: 1,
      }}
    />
    <Typography variant="caption">{value.toFixed(3)}</Typography>
  </Box>
);

const FeatureRanking = ({ model }: { model: MLMetrics }) => {
  const entries = Object.entries(model.feature_importances).sort(
    (a, b) => b[1] - a[1]
  );
  if (entries.length === 0) return null;
  const maxScore = entries.reduce((m, [, v]) => Math.max(m, v), 0) || 1;
  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      <Typography
        sx={{
          fontWeight: 600,
          mb: 1.5,
        }}
      >
        Classement des variables par importance (Random Forest)
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          display: "block",
          mb: 1.5,
        }}
      >
        Modèle : <strong>{getModelLabel(model.model_name)}</strong>.
        L'importance mesure à quel point chaque variable a contribué aux
        décisions des 100 arbres. Les importances des modalités d'une même
        variable sont agrégées.
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  ...cellSx,
                  fontWeight: 700,
                  width: 36,
                  bgcolor: "background.paper",
                }}
              >
                #
              </TableCell>
              <TableCell
                sx={{ ...cellSx, fontWeight: 700, bgcolor: "background.paper" }}
              >
                Variable
              </TableCell>
              <TableCell
                sx={{
                  ...cellSx,
                  fontWeight: 700,
                  bgcolor: "background.paper",
                  textAlign: "right",
                }}
              >
                Importance
              </TableCell>
              <TableCell
                sx={{
                  ...cellSx,
                  fontWeight: 700,
                  bgcolor: "background.paper",
                  width: 160,
                }}
              >
                Poids relatif
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map(([variable, score], idx) => (
              <TableRow
                key={variable}
                sx={{ bgcolor: idx % 2 === 0 ? "action.hover" : "transparent" }}
              >
                <TableCell sx={{ ...cellSx, color: "text.secondary" }}>
                  {idx + 1}
                </TableCell>
                <TableCell sx={{ ...cellSx, fontWeight: 500 }}>
                  {VARIABLE_LABELS[variable] ?? variable}
                </TableCell>
                <TableCell
                  sx={{
                    ...cellSx,
                    textAlign: "right",
                    color: "text.secondary",
                  }}
                >
                  {(score * 100).toFixed(1)}%
                </TableCell>
                <TableCell sx={cellSx}>
                  <ScoreBar value={score} max={maxScore} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default FeatureRanking;
